import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Modal from '../../components/ui/Modal';
import { CheckCircle, XCircle, ArrowDown, Search, Eye, RefreshCw, Send } from 'lucide-react';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import LiveBadge from '../../components/LiveBadge';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const STATUSES = ['pending', 'approved', 'rejected', 'all'];

export default function DepositRequestsPage() {
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialStatus = STATUSES.includes(searchParams.get('status')) ? searchParams.get('status') : 'pending';
  const [status, setStatus] = useState(initialStatus);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [kpiPeriod, setKpiPeriod] = useState('week');
  const [kpis, setKpis] = useState(null);

  useEffect(() => { fetchRequests(); }, [status, search]);
  useEffect(() => { fetchKpis(); /* eslint-disable-next-line */ }, [kpiPeriod]);

  // Live auto-sync: fetch new pending/approved rows every 5s so admins
  // never need to hit refresh. Calls with {silent:true} so the spinner
  // never flashes and the table doesn't blink — data just updates in place.
  useAutoRefresh(() => { fetchRequests({ silent: true }); fetchKpis(); }, 5000, !selected);

  const fetchKpis = async () => {
    try {
      const res = await api.get(`/api/admin/transactions/kpis/requests?type=deposit&period=${kpiPeriod}`);
      setKpis(res.data);
    } catch { /* non-fatal */ }
  };

  const fetchRequests = async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      let url = `/api/admin/transactions/deposits?limit=100&status=${status === 'all' ? '' : status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await api.get(url);
      setRequests(res.data.items || []);
    } catch { if (!silent) toast.error('Failed to load deposits'); }
    finally { if (!silent) setLoading(false); }
  };

  const handleApprove = async (id) => {
    setProcessing(true);
    try {
      await api.post(`/api/admin/transactions/${id}/approve`, { admin_notes: adminNotes });
      toast.success('Deposit approved!');
      setSelected(null); setAdminNotes(''); fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  const handleReject = async (id) => {
    setProcessing(true);
    try {
      await api.post(`/api/admin/transactions/${id}/reject`, { reason: adminNotes });
      toast.success('Deposit rejected');
      setSelected(null); setAdminNotes(''); fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  const [syncingId, setSyncingId] = useState(null);
  const handleSync = async (id) => {
    setSyncingId(id);
    try {
      const res = await api.post(`/api/admin/transactions/${id}/sync-gateway`);
      const status = res?.data?.status?.status || 'UNKNOWN';
      toast.success(`Synced with gateway — status: ${status}`);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Sync failed');
    } finally { setSyncingId(null); }
  };

  const [resendingId, setResendingId] = useState(null);
  const handleResend = async (id) => {
    setResendingId(id);
    try {
      const res = await api.post(`/api/admin/bot-approvals/resend/${id}`);
      const botType = res?.data?.result?.bot_type || 'bot';
      toast.success(`Resent to Telegram (${botType.replace('_', ' ')} bot)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed — configure the bot first in Approval Bots');
    } finally { setResendingId(null); }
  };

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="deposit-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-semibold flex items-center gap-2"><ArrowDown className="w-5 h-5 text-green-400" /> Deposit Requests <LiveBadge />
          {requests.filter(r => r.status === 'pending').length > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">{requests.filter(r => r.status === 'pending').length} pending</span>}
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 mr-1">KPI period:</span>
          {['today', 'yesterday', 'week', 'month'].map(p => (
            <button
              key={p}
              onClick={() => setKpiPeriod(p)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${
                kpiPeriod === p ? 'bg-[#D4AF37] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`deposit-kpi-period-${p}`}
            >
              {p === 'today' ? 'Today' : p === 'yesterday' ? 'Yesterday' : p === 'week' ? 'This Week' : 'This Month'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="deposit-kpi-row">
        <MiniKPI label="Pending" value={(kpis?.pending_count ?? 0).toLocaleString()} hint={`$${(kpis?.pending_amount_usd ?? kpis?.pending_amount ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})} awaiting`} color="#f59e0b" />
        <MiniKPI label="Approved" value={(kpis?.approved_count ?? 0).toLocaleString()} hint="in period" color="#22c55e" />
        <MiniKPI label="Approved Total" value={`$${(kpis?.approved_amount_usd ?? kpis?.approved_amount ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}`} hint="in period" color="#D4AF37" />
        <MiniKPI label="Rejected" value={(kpis?.rejected_count ?? 0).toLocaleString()} hint="in period" color="#ef4444" />
      </div>
      <div className="flex gap-2 flex-wrap">
        <div className="flex rounded-lg p-0.5 bg-[#0B0E14]">
          {STATUSES.map(s => <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition ${status === s ? 'bg-[#D4AF37] text-black' : 'text-gray-400'}`}>{s}</button>)}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by username or email..." className="pl-10 bg-[#0B0E14] border-white/10" />
        </div>
      </div>
      {loading ? <div className="flex justify-center py-8"><div className="spinner" /></div> : requests.length === 0 ? <p className="text-gray-500 py-8 text-center">No deposit requests</p> : (
        <div className="space-y-2">
          {requests.map(req => (
            <div key={req.id} className="glass-card rounded-xl p-4 flex items-center justify-between" data-testid={`deposit-${req.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center flex-shrink-0"><ArrowDown className="w-5 h-5 text-green-400" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-white flex items-center gap-2">
                    {req.username || 'Unknown'}
                    {req.payment_mode === 'GATEWAY' && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300">
                        {req.gateway_provider || 'GATEWAY'} · auto-sync
                      </span>
                    )}
                    {req.last_sync_status && (
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-gray-400"
                        title={`Last sync: ${req.last_sync_at || ''}`}
                      >
                        last: {req.last_sync_status}
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] text-gray-500">{req.email} | {req.payment_method_name || 'N/A'} | {new Date(req.created_at).toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 ml-3">
                <div className="text-right">
                  <p className="text-lg font-bold text-green-400" data-testid={`deposit-amount-${req.id}`}>
                    ${Number(req.amount || 0).toFixed(2)}
                  </p>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${req.status === 'approved' ? 'bg-green-500/20 text-green-400' : req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>{req.status}</span>
                </div>
                {req.status === 'pending' && (
                  <div className="flex gap-2 ml-2 flex-wrap">
                    <button onClick={() => { setSelected(req); setAdminNotes(''); }} className="px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-medium flex items-center gap-1 hover:bg-blue-500/20 transition" data-testid={`view-deposit-${req.id}`}>
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                    {(req.payment_mode === 'GATEWAY' || req.gateway_provider || req.type === 'gateway_deposit') && (
                      <button
                        onClick={() => handleSync(req.id)}
                        disabled={syncingId === req.id}
                        className="px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/30 text-purple-300 text-xs font-medium flex items-center gap-1 hover:bg-purple-500/20 transition disabled:opacity-50"
                        data-testid={`sync-deposit-${req.id}`}
                        title="Sync status with payment gateway"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${syncingId === req.id ? 'animate-spin' : ''}`} /> {syncingId === req.id ? 'Syncing…' : 'Sync'}
                      </button>
                    )}
                    <button onClick={() => handleApprove(req.id)} className="px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-medium flex items-center gap-1 hover:bg-green-500/20 transition" data-testid={`approve-deposit-${req.id}`}>
                      <CheckCircle className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button onClick={() => handleReject(req.id)} className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-medium flex items-center gap-1 hover:bg-red-500/20 transition" data-testid={`reject-deposit-${req.id}`}>
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                    <button
                      onClick={() => handleResend(req.id)}
                      disabled={resendingId === req.id}
                      className="px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-medium flex items-center gap-1 hover:bg-cyan-500/20 transition disabled:opacity-50"
                      data-testid={`resend-deposit-${req.id}`}
                      title="Re-send this request to the configured approval bot on Telegram"
                    >
                      <Send className={`w-3.5 h-3.5 ${resendingId === req.id ? 'animate-pulse' : ''}`} />
                      {resendingId === req.id ? 'Sending…' : 'Resend to Telegram'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal open={!!selected} onClose={() => setSelected(null)} title="Deposit Details" maxWidth="max-w-md">
        {selected && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-[10px] text-gray-500">User</p><p className="font-medium">{selected.username}</p></div>
              <div><p className="text-[10px] text-gray-500">Amount</p><p className="font-bold text-green-400">${Number(selected.amount || 0).toFixed(2)}</p></div>
              <div><p className="text-[10px] text-gray-500">Email</p><p>{selected.email}</p></div>
              <div><p className="text-[10px] text-gray-500">Method</p><p>{selected.payment_method_name || 'N/A'}</p></div>
            </div>
            {selected.proof_image_url && <img src={selected.proof_image_url?.startsWith('http') ? selected.proof_image_url : `${API_BASE}${selected.proof_image_url}`} alt="Proof" className="w-full rounded-lg max-h-64 object-contain bg-black/30" />}
            {selected.status === 'pending' && <>
              <Input value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Admin notes" className="bg-[#0B0E14] border-white/10" />
              <div className="flex gap-2">
                <Button onClick={() => handleApprove(selected.id)} disabled={processing} className="flex-1 bg-green-600">Approve</Button>
                <Button onClick={() => handleReject(selected.id)} disabled={processing} className="flex-1 bg-red-600">Reject</Button>
              </div>
              {(selected.payment_mode === 'GATEWAY' || selected.gateway_provider || selected.type === 'gateway_deposit') && (
                <Button
                  onClick={() => handleSync(selected.id)}
                  disabled={syncingId === selected.id}
                  className="w-full bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 border border-purple-500/40 flex items-center justify-center gap-2"
                  data-testid="modal-sync-gateway"
                >
                  <RefreshCw className={`w-4 h-4 ${syncingId === selected.id ? 'animate-spin' : ''}`} />
                  {syncingId === selected.id ? 'Syncing with gateway…' : 'Sync status with gateway'}
                </Button>
              )}
            </>}
          </div>
        )}
      </Modal>
    </div>
  );
}

function MiniKPI({ label, value, hint = null, color = '#D4AF37' }) {
  return (
    <div
      className="rounded-xl p-3 bg-[#131822] border"
      style={{ borderColor: `${color}33` }}
    >
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className="text-xl font-bold mt-1 truncate" style={{ color }}>{value}</div>
      {hint && <div className="text-[10px] text-gray-500 mt-0.5 truncate">{hint}</div>}
    </div>
  );
}
